import { Link, router } from '@inertiajs/react'
import {
  Plus,
  ExternalLink,
  Eye,
  Pencil,
  Trash2,
  FileText,
  Download,
} from 'lucide-react'
import AppLayout from '@/layouts/AppLayout'
import StatusBadge from '@/components/StatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency, formatDate } from '@/lib/utils'
import Pagination from '@/components/Pagination'
import type { PurchaseOrder, PaginationMeta } from '@/types'

interface Props {
  purchase_orders: PurchaseOrder[]
  filters: { status: string | null }
  pagination: PaginationMeta
}

export default function PurchaseOrdersIndex({ purchase_orders, filters, pagination }: Props) {
  function handleFilterChange(value: string) {
    router.get(
      '/purchase_orders',
      { status: value === 'all' ? null : value },
      { preserveState: true, preserveScroll: true }
    )
  }

  function handleDelete(id: number) {
    if (confirm('Are you sure you want to delete this purchase order?')) {
      router.delete(`/purchase_orders/${id}`)
    }
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Purchase Orders</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and track all purchase orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <a href={`/purchase_orders/csv${filters.status ? `?status=${filters.status}` : ''}`} download>
              <Download className="size-4" />
              CSV
            </a>
          </Button>
          <Button asChild size="sm">
            <Link href="/purchase_orders/new">
              <Plus className="size-4" />
              New Purchase Order
            </Link>
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="border-border/50 mb-6">
        <CardContent className="flex items-center gap-4 pt-6">
          <span className="text-sm font-medium text-muted-foreground">Filter by status:</span>
          <Select
            value={filters.status || 'all'}
            onValueChange={handleFilterChange}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="estimated">Estimated</SelectItem>
              <SelectItem value="finalized">Finalized</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Data table */}
      <Card className="border-border/50">
        <CardContent className="pt-0">
          {purchase_orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <FileText className="size-10 mb-3 opacity-30" />
              <p className="text-sm font-medium">No purchase orders found</p>
              <p className="text-xs mt-1">Create a new purchase order to get started</p>
              <Button asChild size="sm" className="mt-4">
                <Link href="/purchase_orders/new">
                  <Plus className="size-4" />
                  New Purchase Order
                </Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">ID</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Status</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Sheet</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchase_orders.map((po) => (
                  <TableRow key={po.id} className="border-border/30">
                    <TableCell>
                      <Link
                        href={`/purchase_orders/${po.id}`}
                        className="text-primary hover:underline font-medium"
                      >
                        #{po.id}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-sm font-medium">
                      {formatCurrency(parseFloat(po.amount))}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={po.status} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(po.date)}
                    </TableCell>
                    <TableCell>
                      {po.google_sheet_url ? (
                        <a
                          href={po.google_sheet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
                        >
                          <ExternalLink className="size-3.5" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground/40">--</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button asChild variant="ghost" size="icon-xs">
                          <Link href={`/purchase_orders/${po.id}`}>
                            <Eye className="size-3.5" />
                          </Link>
                        </Button>
                        <Button asChild variant="ghost" size="icon-xs">
                          <Link href={`/purchase_orders/${po.id}/edit`}>
                            <Pencil className="size-3.5" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => handleDelete(po.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Pagination pagination={pagination} baseUrl="/purchase_orders" params={{ status: filters.status }} />
    </AppLayout>
  )
}
